import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Target, Eye, Compass } from 'lucide-react';
import '../styles/fonts.css';

export default function MissionVision() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const y1 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.5, 1, 1, 0.5]);

  return (
    <section 
      id="mission" 
      ref={ref}
      className="relative py-16 overflow-hidden"
    >
      {/* Parallax Background Layers */}
      <motion.div 
        className="absolute inset-0 sm:bg-fit bg-cover bg-center"
        style={{ 
          // backgroundImage: 'url(https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1920&q=80)',
          backgroundImage: 'url(/images/missionvision.jpeg)',
         // y: y1 
          
        }}
      />
      <motion.div 
        className="absolute inset-0 bg-gradient-to-b from-emerald-900/90 via-teal-900/85 to-emerald-900/90"
        style={{ opacity }}
      />
      
      {/* Floating Elements */}
      <motion.div
        className="absolute top-20 left-10 w-64 h-64 bg-emerald-400/10 rounded-full blur-3xl"
        style={{ y: y2 }}
      />
      <motion.div
        className="absolute bottom-20 right-10 w-80 h-80 bg-teal-400/10 rounded-full blur-3xl"
        style={{ y: y3 }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <motion.div
          className="text-center mt-6 mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-emerald-300 font-medium text-sm uppercase tracking-wider">Our Purpose</span>
          <h2 className="text-4xl md:text-5xl kaushan-script  font-bold text-white mt-3">
            Mission & Vision
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Mission */}
          <motion.div
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ y: y3 }}
          >
            <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 mb-6">
              <Target className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
            <p className="text-white/80 fuzzy-bubbles leading-relaxed">
              The mission is to offer restorative care that fosters independence, boosts self-esteem, enhances self-image, 
              and supports overall well-being and quality of life. This is achieved through restorative therapy, compassionate support, 
              and building connections within the community.
            </p>
          </motion.div>

          {/* Vision */}
          <motion.div
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 md:-translate-y-8"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
             style={{ y: y3 }}
          >
            <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-orange-400 to-rose-500 mb-6">
              <Eye className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
            <p className="text-white/80 fuzzy-bubbles leading-relaxed">
             To provide a collaborative, team-based approach to senior care through high-quality restorative programs. 
              Through compassion, ongoing education, and partnerships with allied healthcare professionals, Restorative 
              Connections strives to support seniors in navigating life’s challenges across all healthcare settings. 
            </p>
          </motion.div>

          {/* Values */}
          <motion.div
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{ y: y3 }}
          >
            <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-purple-400 to-indigo-500 mb-6">
              <Compass className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Our Values</h3>
            <p className="text-white/80 fuzzy-bubbles leading-relaxed">
              Compassion, Empowerment, Collaboration, Integrity, and Excellence guides everything we do. 
              We believe in transparency, continuous learning, and treating every individual with dignity and respect.
            </p>
          </motion.div>
        </div>

        {/* Quote */}
        <motion.blockquote
          className="mt-4 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-2xl md:text-3xl kaushan-script  text-white/90 font-light italic rounded-xl border-1 bg-purple-800 max-w-3xl mx-auto leading-relaxed">
            Restorative Therapy gives back “Quality of Life".
          </p>
          <cite className="block mt-4 text-emerald-300 font-medium not-italic">
            — Our Founding Philosophy
          </cite>
        </motion.blockquote>
      </div>
    </section>
  );
}